import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../utils/config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Building2, 
  Users, 
  TrendingUp, 
  CreditCard, 
  CalendarDays,
  ArrowRight,
  RefreshCw,
  Loader2,
  AlertCircle
} from 'lucide-react';

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = getBackendUrl();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('horeca_token');
      const response = await axios.get(`${API_URL}/api/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError(err.response?.data?.detail || 'Kon statistieken niet laden');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
              Admin Dashboard
            </h1>
            <p className="text-[#57534E] mt-1">
              Overzicht van alle bedrijven en abonnementen
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchStats}
            disabled={loading}
            data-testid="refresh-stats-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Vernieuwen
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-500" />
          <p className="text-rose-700">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && !stats && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#C2410C]" />
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <>
          <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            {/* Companies Today */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#57534E] mb-1">Nieuwe bedrijven vandaag</p>
                    <p className="text-3xl font-bold text-[#1C1917]" data-testid="companies-today">
                      {stats.companies_today}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <CalendarDays className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Companies This Month */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#57534E] mb-1">Nieuwe bedrijven deze maand</p>
                    <p className="text-3xl font-bold text-[#1C1917]" data-testid="companies-month">
                      {stats.companies_month}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Companies */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#57534E] mb-1">Totaal bedrijven</p>
                    <p className="text-3xl font-bold text-[#1C1917]" data-testid="total-companies">
                      {stats.total_companies}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <div className="mt-3">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {stats.active_subscriptions} actief
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Stripe Revenue */}
            <Card className="card-base">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#57534E] mb-1">Omzet deze maand</p>
                    {stats.stripe_configured ? (
                      <p className="text-3xl font-bold text-[#1C1917]" data-testid="stripe-revenue">
                        {formatCurrency(stats.stripe_revenue_month)}
                      </p>
                    ) : (
                      <p className="text-lg text-amber-600">Stripe niet geconfigureerd</p>
                    )}
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-[#C2410C]/10 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-[#C2410C]" />
                  </div>
                </div>
                {stats.stripe_error && (
                  <p className="text-xs text-amber-600 mt-2">{stats.stripe_error}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            <Card 
              className="card-base cursor-pointer hover:border-[#C2410C]/30 transition-colors"
              onClick={() => navigate('/admin/companies')}
              data-testid="goto-companies"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1917]">Alle Bedrijven</h3>
                      <p className="text-sm text-[#57534E]">Bekijk en beheer alle bedrijven</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#57534E]" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="card-base cursor-pointer hover:border-[#C2410C]/30 transition-colors"
              onClick={() => navigate('/admin/subscriptions')}
              data-testid="goto-subscriptions"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1917]">Subscriptions</h3>
                      <p className="text-sm text-[#57534E]">Beheer abonnementen en cadeaus</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#57534E]" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="card-base cursor-pointer hover:border-[#C2410C]/30 transition-colors"
              onClick={() => navigate('/admin/settings')}
              data-testid="goto-settings"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1917]">Systeem Instellingen</h3>
                      <p className="text-sm text-[#57534E]">Email, Stripe en database status</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#57534E]" />
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboardPage;
